import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useEffect, useMemo } from 'react'
import { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import SystemDetails from 'src/Libs/installation-details/components/system-details/SystemDetails'
import HardwareComponents from 'src/Libs/installation-details/components/hardware-components/HardwareComponents'
import InstallationStatements from 'src/Libs/installation-details/components/installation-statements/InstallationStatements'
import ErrorLogs from 'src/Libs/installation-details/components/ErrorLogs'
import useInstallationStatements from 'src/Libs/installations/hooks/use-installation-statements'

interface UseSystemDetailsTabs {
  tabs: TabsBarTab[]
  setTabs: (value: TabsBarTab[]) => void
  setActiveTab: (value: number) => void
}

/**
 * Hook that contains tabs for the system details of a storage
 * @param installationId - installation id
 * @param storageId - storage serialnumber
 * @param hasInstallationStatement - if there is an installation statement for this storage or not
 */
export const useSystemDetailsTabs = (installationId?: string, storageId?: string): UseSystemDetailsTabs => {
  const { setTabs, setActiveTab } = useHeaderTabs()
  const { storageDeviceLink } = useActiveInstallationContext()
  const { currentStorageInstallationStatement: installationStatement, refetchCurrentStorageInstallationStatement } = useInstallationStatements(
    installationId ? Number(installationId) : undefined,
    storageDeviceLink?.id
  )

  const { t } = useCustomTranslation()

  const tabs = useMemo(
    () => [
      {
        label: t('system-details.informations.title'),
        href: `/overview/installations/${installationId}/${storageId}/details`,
        componentHref: `/details`,
        component: <SystemDetails />,
      },
      {
        label: t('system-details.components.title'),
        href: `/overview/installations/${installationId}/${storageId}/components`,
        componentHref: `/components`,
        component: <HardwareComponents />,
      },
      {
        label: t('system-details.error-logs.title'),
        href: `/overview/installations/${installationId}/${storageId}/error-logs`,
        componentHref: `/error-logs`,
        component: <ErrorLogs />,
      },
      {
        label: t('system-details.installation-statements.title'),
        href: `/overview/installations/${installationId}/${storageId}/statements`,
        componentHref: `/statements`,
        component: (
          <InstallationStatements
            currentStorageInstallationStatement={installationStatement}
            refetchCurrentStorageInstallationStatement={refetchCurrentStorageInstallationStatement}
          />
        ),
      },
    ],
    [t, installationId, storageId, installationStatement, refetchCurrentStorageInstallationStatement]
  )

  useEffect(() => {
    setTabs(tabs)

    return () => {
      setTabs([])
    }
  }, [setTabs, tabs])

  return { tabs, setTabs, setActiveTab }
}
